import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "src/store";
import { thunks } from "src/thunks/assets";
import { slice } from "src/slices/assets";
import { spacesDOSettingsThunks } from "src/thunks/spaces-do-settings";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
} from "@mui/material";
import { Folder, File } from "lucide-react";
import { withAssetsGuard } from "src/hocs/with-role-based-guard.js";
import { Container, Stack } from "@mui/system";
import { CustomButton, CustomTypography } from "src/components/inputs/index.js";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
// Icons
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import tmwWarningIcon from "src/icons/tmw-ui/tmw-warning-icon.png";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Check,
  CloseOutlined,
  Delete,
  DeleteOutline,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useAuth } from "src/hooks/use-auth.js";
import { Seo } from "src/components/seo.js";
import { assetsPermission } from "src/guards/role-based-guard";
// Modals
import { AssetsViewHistoryModal } from "./modals/assets-view-history-modal";

const Assets = () => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const outerTheme = useTheme();

  // Get User Permissions
  const userPermissions = user?.permissions;
  const hasAdminPermission = userPermissions.find(
    (userPermission) => userPermission.key === assetsPermission.admin,
  );

  const {
    items,
    itemsLoading,
    filesUploading,
    filesUploadErrors,
    itemDeleting,
    multipleItemsDeleting,
    closeAddItemModal,
    closeDeleteItemModal,
    closeDeleteMultipleSelectedItemsModal,
    spacesApiHistoryLog,
    spacesApiHistoryLogLoading,
  } = useSelector((state) => state.assets);

  const { spacesDOSettings, spacesDOSettingsLoading } = useSelector(
    (state) => state.spacesDOSettings,
  );

  const [path, setPath] = useState("");

  // Hovering
  const [hoveredElement, setHoveredElement] = useState("");

  // Image Preview on Hover
  const [hoveredImage, setHoveredImage] = useState(null);
  const hoverImageDelay = 500; // 0.5 seconds
  const timerRef = useRef(null);

  // New Folder / Directory
  const [addingDirectory, setAddingDirectory] = useState(false);
  const [newDirectoryName, setNewDirectoryName] = useState("");
  const newDirectoryRef = useRef(null);

  // Upload Files
  const [selectedFilesForUpload, setSelectedFilesForUpload] = useState(null);
  const [uploadFilesTotalSize, setUploadFilesTotalSize] = useState(0);
  const [duplicatedFilesForUpload, setDuplicatedFilesForUpload] =
    useState(null);

  // Add File
  const [addItemModalVisible, setAddItemModalVisible] = useState(false);

  // Delete Folder / File (Single)
  const [deleteItems, setDeleteItems] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);

  // Delete File (Multiple)
  const [selectedItemsToDelete, setSelectedItemsToDelete] = useState([]);
  const [deleteMultipleItemsModalOpened, setDeleteMultipleItemsModalOpened] =
    useState(false);

  // Alerts
  const [showWrongFileTypeAlert, setShowWrongFileTypeAlert] = useState(false);

  // Assets History Modal
  const [assetsHistoryModalShow, setAssetsHistoryModalShow] = useState(false);

  // Search
  const [searchFileName, setSearchFileName] = useState("");

  // Display Items Layout (Number of Rows)
  const columnWidth = 350;
  const [numRows, setNumRows] = useState(1);

  //
  // Filtering - Start
  //

  let filteredItems = items;

  const applySearchFileName = () => {
    if (searchFileName) {
      filteredItems = filteredItems.filter((file) =>
        file?.name?.toLowerCase().includes(searchFileName.toLowerCase()),
      );
    }
  };

  // Apply Filtering
  applySearchFileName();

  useEffect(() => {
    const updateRows = () => {
      const containerWidth = window.innerWidth; // adjust for margin/padding
      const numColumns = Math.max(1, Math.floor(containerWidth / columnWidth));
      const rows = Math.ceil(filteredItems.length / numColumns);
      setNumRows(rows);
    };

    updateRows(); // run on mount
    window.addEventListener("resize", updateRows); // re-run on resize

    return () => window.removeEventListener("resize", updateRows);
  }, [filteredItems]);

  //
  // Filtering - End
  //

  useEffect(() => {
    if (!selectedFilesForUpload) {
      setUploadFilesTotalSize(0);
      setDuplicatedFilesForUpload(null);

      return;
    }

    let sum = 0;

    const uploadedFilenames = [];

    // Iterate over FormData entries
    selectedFilesForUpload.forEach((value, key) => {
      if (value && value.name && value.size) {
        sum = sum + value.size;

        // Push to Uploaded Filenames
        uploadedFilenames.push({
          name: value.path,
          overwrite: false,
          addNewFile: false,
        });
      }
    });

    setUploadFilesTotalSize(sum);

    //
    // Compare Uploaded Filenames with Existing Filenames in current (Folder) - Find Duplicates Filenames
    //

    const duplicatesFilenames = uploadedFilenames.filter((n) =>
      items.some((f) => f.name === n.name),
    );

    if (duplicatesFilenames.length > 0) {
      setDuplicatedFilesForUpload(duplicatesFilenames);
    }
  }, [selectedFilesForUpload]); // Only run when `selectedFilesForUpload` changes

  //
  // Search Bar - Start
  //

  const customTheme = (outerTheme) =>
    createTheme({
      palette: {
        mode: outerTheme.palette.mode,
      },
      components: {
        MuiTextField: {
          styleOverrides: {
            root: {
              "--TextField-brandBorderColor": "#E0E3E7",
              "--TextField-brandBorderHoverColor": "#B2BAC2",
              "--TextField-brandBorderFocusedColor": "#6F7E8C",
              "& label.Mui-focused": {
                color: "var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: "var(--TextField-brandBorderColor)",
            },
            root: {
              [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderHoverColor)",
              },
              [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              "&::before, &::after": {
                borderBottom: "2px solid var(--TextField-brandBorderColor)",
              },
              "&:hover:not(.Mui-disabled, .Mui-error):before": {
                borderBottom:
                  "2px solid var(--TextField-brandBorderHoverColor)",
              },
              "&.Mui-focused:after": {
                borderBottom:
                  "2px solid var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
        MuiInput: {
          styleOverrides: {
            root: {
              "&::before": {
                borderBottom: "2px solid var(--TextField-brandBorderColor)",
              },
              "&:hover:not(.Mui-disabled, .Mui-error):before": {
                borderBottom:
                  "2px solid var(--TextField-brandBorderHoverColor)",
              },
              "&.Mui-focused:after": {
                borderBottom:
                  "2px solid var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
      },
    });

  const renderSearchBar = () => {
    return (
      <Box component="form" noValidate autoComplete="off">
        <ThemeProvider
          theme={customTheme(outerTheme)}
          style={{
            fontFamily: "tmwCustomFont",
            textTransform: "uppercase",
            letterSpacing: 1,
            fontSize: 10,
          }}
        >
          <TextField
            label="Search"
            color="warning"
            variant="standard"
            onChange={(e) => setSearchFileName(e.target.value)}
            value={searchFileName}
            style={{
              fontFamily: "tmwCustomFont",
              textTransform: "uppercase",
              letterSpacing: 1,
              fontSize: 10,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={() => searchFileName && setSearchFileName("")}
                    edge="end"
                    size="small"
                    disabled={!searchFileName}
                    sx={{
                      opacity: searchFileName ? 1 : 0.4,
                      cursor: searchFileName ? "pointer" : "default",
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </ThemeProvider>
      </Box>
    );
  };

  //
  // Search Bar - End
  //

  //
  // Upload Files On Drop - Start
  //

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length) {
        setShowWrongFileTypeAlert(true);
        setTimeout(() => setShowWrongFileTypeAlert(false), 2000);
        return;
      }

      const formData = new FormData();

      acceptedFiles.forEach((file) => {
        formData.append("files", file);
      });

      formData.append("filePath", path);

      setSelectedFilesForUpload(formData);
    },
    [path],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    useFsAccessApi: false,
    webkitdirectory: true, // Allows folder selection
    directory: true, // Some browsers need this
    multiple: true, // Allow multiple files/folders
    // accept: { "image/*": [".jpg", ".jpeg", ".png"] }, // Restrict to images if needed
  });

  //
  // Upload Files On Drop - End
  //

  const updateData = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathFromUrl = urlParams.get("path");

    if (pathFromUrl) {
      setPath(pathFromUrl);
    }

    dispatch(thunks.getItems(pathFromUrl || ""));

    // Get Spaces Api History Logs if user has Admin Permission
    if (hasAdminPermission) {
      dispatch(thunks.getSpacesApiHistoryLog());
    }
  };

  useEffect(() => {
    dispatch(slice.actions.setItems([]));
    // Fetch Spaces DO Settings
    dispatch(spacesDOSettingsThunks.getSpacesDOSettings());

    updateData();
  }, [path]);

  // Trigger Refresh After Upload is finished
  useEffect(() => {
    if (closeAddItemModal) {
      setAddItemModalVisible(false);
      dispatch(thunks.closeAddItemModalReset());
      updateData();
      setSelectedFilesForUpload(null);
    }
  }, [closeAddItemModal]);

  // Trigger Refresh After Delete Item is finished
  useEffect(() => {
    if (closeDeleteItemModal) {
      setSelectedItemsToDelete([]);
      setDeleteMultipleItemsModalOpened(false);
      setDeleteItems(false);
      dispatch(thunks.closeDeleteMultipleSelectedItemsModalReset());
      updateData();
    }
  }, [closeDeleteItemModal]);

  // Trigger Refresh After Delete Multiple Selected Items is finished
  useEffect(() => {
    if (closeDeleteMultipleSelectedItemsModal) {
      setSelectedItemsToDelete([]);
      setDeleteMultipleItemsModalOpened(false);
      setDeleteItems(false);
      dispatch(thunks.closeDeleteMultipleSelectedItemsModalReset());
      updateData();
    }
  }, [closeDeleteMultipleSelectedItemsModal]);

  useEffect(() => {
    if (newDirectoryRef?.current) {
      newDirectoryRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [addingDirectory]);

  const viewItem = async (filePath) => {
    dispatch(thunks.viewItem(filePath));
  };

  const uploadFolder = async (folderName) => {
    try {
      await dispatch(thunks.createFolder(folderName));

      updateData();
    } catch (error) {
      console.log(error);
    }
  };

  const deleteFolder = async (folderPath) => {
    try {
      await dispatch(thunks.deleteFolder(folderPath));

      setItemToDelete(null);
      setDeleteModalOpened(false);
      setDeleteItems(false);
      updateData();
    } catch (error) {
      console.log(error);
    }
  };

  const deleteItem = async (filePath) => {
    try {
      await dispatch(thunks.deleteItem(filePath));

      setItemToDelete(null);
      setDeleteModalOpened(false);
      setDeleteItems(false);
      updateData();
    } catch (error) {
      console.log(error);
    }
  };

  const deleteMultipleItems = async (filesPathArray) => {
    try {
      dispatch(thunks.deleteMultipleSelectedItems(filesPathArray));
    } catch (error) {
      console.log(error);
    }
  };

  const navigateTo = (folder) => {
    setPath((prev) => {
      const newPath = prev ? `${prev}${folder}/` : `${folder}/`;

      window.history.pushState(
        { path: newPath },
        "",
        `?path=${encodeURIComponent(newPath)}`,
      );

      return newPath;
    });
  };

  const goBack = () => {
    handleCloseAddDirectory();
    setDeleteItems(false);

    if (!path) return;

    const parts = path.split("/").filter(Boolean);
    if (parts.length > 0) {
      parts.pop();
    }

    const newPath = parts.length ? `${parts.join("/")}/` : "";

    setPath(newPath);

    window.history.replaceState(
      { path: newPath },
      "",
      newPath
        ? `?path=${encodeURIComponent(newPath)}`
        : window.location.pathname,
    );
  };

  const renderBackButton = () => {
    return (
      <Stack
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Stack
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            cursor: "pointer",
            textDecoration:
              hoveredElement === "back-button" ? "underline" : "none",
            padding: 5,
          }}
          onMouseEnter={() => setHoveredElement("back-button")}
          onMouseLeave={() => setHoveredElement()}
          onClick={goBack}
        >
          <KeyboardBackspaceIcon />
          <CustomTypography message="Back" style={{ fontSize: 12 }} />
        </Stack>
      </Stack>
    );
  };

  // Create Directory / Folder Button
  const renderCreateDirectoryButton = () => {
    return (
      <CustomButton
        style={{ background: "white", width: "180px" }}
        label="Create Folder"
        onClick={() => {
          setDeleteItems(false);
          setAddingDirectory(true);
        }}
        disabled={addingDirectory}
      />
    );
  };

  // Delete Item Button
  const renderDeleteItemButton = () => {
    return (
      <CustomButton
        style={{
          width: "180px",
          background: deleteItems ? "#FF0000 !important" : "white !important",
        }}
        labelStyle={{
          color: deleteItems ? "white" : "black",
        }}
        label={deleteItems ? "Cancel Delete" : "Delete"}
        onClick={() => {
          if (deleteItems) {
            setDeleteItems(false);
            setSelectedItemsToDelete([]);
          } else {
            setDeleteItems(true);
          }

          setAddingDirectory(false);
        }}
        disabled={path.split("/").length < 2}
      />
    );
  };

  // Delete Multiple Selection Button
  const renderDeleteMultipleFilesButton = () => {
    return (
      <CustomButton
        style={{
          width: "180px",
          background:
            selectedItemsToDelete?.length > 0
              ? "#39B349 !important"
              : "white !important",
        }}
        labelStyle={{
          color: selectedItemsToDelete?.length > 0 ? "white" : "black",
        }}
        label={"Delete Selected"}
        onClick={() => {
          setAddingDirectory(false);
          setDeleteMultipleItemsModalOpened(true);
        }}
        disabled={selectedItemsToDelete?.length === 0}
      />
    );
  };

  // Handle Export Excel Files
  const handleExportExcelFiles = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathFromUrl = urlParams.get("path");

    dispatch(thunks.exportFilesToExcel(pathFromUrl || ""));
  };

  // Export Excel Button
  const renderExportExcelButton = () => {
    return (
      <CustomButton
        style={{ background: "white", width: "180px" }}
        label="Export Excel"
        onClick={() => handleExportExcelFiles()}
        disabled={addingDirectory}
      />
    );
  };

  // Assets Spaces History Button
  const renderHistoryButton = () => {
    return (
      <CustomButton
        style={{ background: "white", width: "180px" }}
        label="History"
        onClick={() => handleOpenAssetsHistoryModal()}
        disabled={addingDirectory}
      />
    );
  };

  // Handle Open Assets History Modal
  const handleOpenAssetsHistoryModal = () => {
    setAssetsHistoryModalShow(true);
  };

  // Handle Close Assets History Modal
  const handleCloseAssetsHistoryModal = () => {
    setAssetsHistoryModalShow(false);
  };

  const renderAssetsHistoryModal = () => {
    return (
      <>
        {assetsHistoryModalShow && (
          <AssetsViewHistoryModal
            title={"Assets History"}
            cancelButtonLabel={"Close"}
            cancelButton={handleCloseAssetsHistoryModal}
            avatarBackgroundColor={"error.lightest"}
            avatarColor={"error.main"}
            successButtonBackgroundColor={"error.main"}
            successButtonBackgroundColorHover={"error.dark"}
            isOpen={assetsHistoryModalShow}
            histories={spacesApiHistoryLog}
            path={path}
          />
        )}
      </>
    );
  };

  const handleAddItem = () => {
    setAddItemModalVisible(true);
  };

  const handleUploadFiles = async () => {
    if (selectedFilesForUpload) {
      //
      // Append Duplicated Files For Upload to Form Data
      //

      selectedFilesForUpload.append(
        "duplicatedFilesForUpload",
        duplicatedFilesForUpload
          ? JSON.stringify(duplicatedFilesForUpload)
          : null,
      );
      dispatch(thunks.uploadFiles(selectedFilesForUpload));
      updateData();
    }
  };

  const renderAddItemsButton = () => {
    return (
      <CustomButton
        style={{ background: "white", width: "180px" }}
        label="Upload Files"
        onClick={handleAddItem}
      />
    );
  };

  const handleCloseAddDirectory = () => {
    setAddingDirectory(false);
    setNewDirectoryName("");
    setDeleteItems(false);
  };

  //
  // Button Section - Upload / Delete / Create Folder - Start
  //

  const renderButtonSection = () => {
    return (
      <>
        <Stack
          style={{
            right: 25,
            position: "fixed",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          {!deleteItems ? renderAddItemsButton() : <></>}
          {!deleteItems ? renderExportExcelButton() : <></>}
          {hasAdminPermission && !deleteItems ? renderHistoryButton() : <></>}
          {renderDeleteItemButton()}
          {deleteItems ? renderDeleteMultipleFilesButton() : <></>}
          {!deleteItems ? renderCreateDirectoryButton() : <></>}
        </Stack>
        <Stack
          style={{
            top: 250,
            right: 25,
            position: "fixed",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          {deleteItems ? renderSelectAllFilesButton() : <></>}
          {deleteItems ? renderUnselectAllFilesButton() : <></>}
        </Stack>
      </>
    );
  };

  //
  // Button Section - Upload / Delete / Create Folder - End
  //

  //
  // Button Section - Select All Files / Unselect All Files - Start
  //

  const renderSelectAllFilesButton = () => {
    // Generate Files available for Delete (no folders)
    const filesToDelete = items.filter((item) => item.isFolder === false);

    return (
      <CustomButton
        style={{
          width: "200px",
          background:
            filesToDelete?.length > 0 ? "black !important" : "white !important",
        }}
        labelStyle={{
          color: filesToDelete?.length > 0 ? "white" : "black",
        }}
        label="Select All Files"
        onClick={() => setSelectedItemsToDelete(filesToDelete)}
        disabled={filesToDelete?.length === 0 ? true : false}
      />
    );
  };

  const renderUnselectAllFilesButton = () => {
    return (
      <CustomButton
        style={{
          width: "200px",
          background:
            selectedItemsToDelete?.length > 0
              ? "black !important"
              : "white !important",
        }}
        labelStyle={{
          color: selectedItemsToDelete?.length > 0 ? "white" : "black",
        }}
        label="Unselect All Files"
        onClick={() => setSelectedItemsToDelete([])}
        disabled={selectedItemsToDelete?.length === 0 ? true : false}
      />
    );
  };

  //
  // Button Section - Select All Files / Unselect All Files - End
  //

  const renderDropzone = (getRootProps, getInputProps, isDragActive) => {
    return (
      <Stack>
        <Stack
          p={5}
          {...getRootProps()}
          style={{
            width: "100%",
            height: "100px",
            border: "1px dashed lightgrey",
            fontFamily: "tmwCustomFont",
            textTransform: "uppercase",
            fontSize: 12,
            textAlign: "center",
            cursor: "pointer",
            userSelect: "none",

            // // Blurring The Dropzone Box on Upload
            // filter: filesUploading ? "blur(1px)" : "none",
            // pointerEvents: filesUploading ? "none" : "auto",
          }}
        >
          <input {...getInputProps()} disabled={filesUploading} />
          {!showWrongFileTypeAlert ? (
            isDragActive ? (
              <Stack
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "lightgrey",
                }}
              >
                {!filesUploading && (
                  <div>
                    <CloudUploadIcon
                      style={{ height: "40px", width: "40px" }}
                    />
                    <Stack style={{ fontSize: 10 }}>
                      Click / Drag and drop to attach folder or file
                    </Stack>
                  </div>
                )}

                {filesUploading && (
                  <div>
                    <CircularProgress
                      color="inherit"
                      size={13}
                      style={{ marginRight: 5 }}
                    />
                    <CustomTypography
                      message={`Uploading`}
                      style={{ fontSize: 13 }}
                    />
                  </div>
                )}
              </Stack>
            ) : (
              <Stack
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "black",
                }}
              >
                {!filesUploading && (
                  <div>
                    <CloudUploadIcon
                      style={{ height: "40px", width: "40px" }}
                    />
                    <Stack style={{ fontSize: 10 }}>
                      Click / Drag and drop to attach folder or file
                    </Stack>
                  </div>
                )}

                {filesUploading && (
                  <div>
                    <CircularProgress
                      color="inherit"
                      size={13}
                      style={{ marginRight: 5 }}
                    />
                    <CustomTypography
                      message={`Uploading`}
                      style={{ fontSize: 13 }}
                    />
                  </div>
                )}
              </Stack>
            )
          ) : (
            <Stack
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "red",
              }}
            >
              Choose a valid input
            </Stack>
          )}
        </Stack>
        {selectedFilesForUpload && (
          <Stack
            style={{
              alignItems: "center",
              justifyContent: "flex-start",
              fontSize: 12,
              marginTop: "10px",
              width: "100%",
              gap: 5,
            }}
          >
            <CustomTypography message="Selected files:" />
            <Stack
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                width: "100%",
              }}
            >
              {renderFormDataFiles(selectedFilesForUpload)}
            </Stack>
            <div>
              {(uploadFilesTotalSize / (1000 * 1000)).toFixed(2) >
              spacesDOSettings?.maxUploadLimitMB ? (
                <CustomTypography
                  message={`Warning: The total size of the selected files (${(
                    uploadFilesTotalSize /
                    (1000 * 1000)
                  ).toFixed(2)} MB) exceeds the maximum allowed size (${
                    spacesDOSettings?.maxUploadLimitMB
                  } MB). Please select a smaller number of files and try again to upload them.`}
                  style={{ color: "red" }}
                />
              ) : (
                <CustomTypography
                  message={`Files size within the limit (${(
                    uploadFilesTotalSize /
                    (1000 * 1000)
                  ).toFixed(2)} MB / ${spacesDOSettings?.maxUploadLimitMB} MB)`}
                  style={{ color: "green" }}
                />
              )}
            </div>
          </Stack>
        )}
      </Stack>
    );
  };

  // Render Form Data Files on Upload
  const renderFormDataFiles = (formData) => {
    const files = [];

    // Iterate over FormData entries
    formData.forEach((value, key) => {
      if (value && value.name && value.size) {
        files.push(value.name);
      }
    });

    return (
      <TableContainer
        style={{ border: "1px solid #E6E6E6", borderRadius: "1px" }}
      >
        <Table stickyHeader size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 8,
                }}
              >
                N°
              </TableCell>
              <TableCell
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 8,
                }}
              >
                File Name
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file, index) => (
              <TableRow
                key={file}
                sx={{
                  "& td, & th": {
                    padding: "2px 8px", // smaller vertical and horizontal padding
                  },
                }}
              >
                <TableCell
                  align="left"
                  style={{
                    fontFamily: "tmwCustomFont",
                    letterSpacing: 1,
                    fontSize: 11,
                  }}
                >
                  {index + 1}
                </TableCell>
                <TableCell
                  align="left"
                  style={{
                    fontFamily: "tmwCustomFont",
                    letterSpacing: 1,
                    fontSize: 11,
                    whiteSpace: "normal", // allow text to wrap
                    wordBreak: "break-word", // break long words if needed
                  }}
                >
                  {file}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Duplicated Form Data Files (Overwrite / Add New File Checkboxes Handle)
  const handleOverwriteOrAddNewFileCheckboxesForDuplicatedFiles =
    (index, key) => (e) => {
      if (duplicatedFilesForUpload && duplicatedFilesForUpload.length > 0) {
        const updatedArray = [...duplicatedFilesForUpload];
        const isChecked = e.target.checked;

        // Reset only the checkboxes of the clicked item
        updatedArray[index].overwrite = false;
        updatedArray[index].addNewFile = false;

        // Set only the clicked one
        updatedArray[index][key] = isChecked;

        setDuplicatedFilesForUpload(updatedArray);
      }
    };

  // Render Duplicated Form Data Files on Upload
  const renderDuplcatedFormDataFiles = (data) => {
    return (
      <TableContainer
        style={{ border: "1px solid #E6E6E6", borderRadius: "1px" }}
      >
        <Table stickyHeader size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 8,
                }}
              >
                N°
              </TableCell>
              <TableCell
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 8,
                }}
              >
                File Name
              </TableCell>
              <TableCell
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 8,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.textDecoration = "underline")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.textDecoration = "none")
                }
                onClick={() => {
                  if (!filesUploading) {
                    setDuplicatedFilesForUpload((prev) =>
                      prev.map((row) => ({
                        ...row,
                        overwrite: true,
                        addNewFile: false,
                      })),
                    );
                  }
                }}
              >
                Overwrite
              </TableCell>
              <TableCell
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 8,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.textDecoration = "underline")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.textDecoration = "none")
                }
                onClick={() => {
                  if (!filesUploading) {
                    setDuplicatedFilesForUpload((prev) =>
                      prev.map((row) => ({
                        ...row,
                        overwrite: false,
                        addNewFile: true,
                      })),
                    );
                  }
                }}
              >
                New File
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={row.name}
                sx={{
                  "& td, & th": {
                    padding: "2px 8px", // smaller vertical and horizontal padding
                  },
                }}
              >
                <TableCell
                  align="left"
                  style={{
                    fontFamily: "tmwCustomFont",
                    letterSpacing: 1,
                    fontSize: 11,
                  }}
                >
                  {index + 1}
                </TableCell>
                <TableCell
                  align="left"
                  style={{
                    fontFamily: "tmwCustomFont",
                    letterSpacing: 1,
                    fontSize: 11,
                    whiteSpace: "normal", // allow text to wrap
                    wordBreak: "break-word", // break long words if needed
                  }}
                >
                  {row.name}
                </TableCell>
                <TableCell
                  align="center"
                  component="th"
                  scope="row"
                  style={{
                    fontFamily: "tmwCustomFont",
                    letterSpacing: 1,
                    fontSize: 11,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={row.overwrite}
                        disabled={filesUploading}
                        onChange={handleOverwriteOrAddNewFileCheckboxesForDuplicatedFiles(
                          index,
                          "overwrite",
                        )}
                        style={{
                          color: "black",
                          opacity: filesUploading ? 0.4 : 1,
                        }}
                      />
                    }
                  />
                </TableCell>
                <TableCell
                  align="center"
                  style={{
                    fontFamily: "tmwCustomFont",
                    letterSpacing: 1,
                    fontSize: 11,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={row.addNewFile}
                        disabled={filesUploading}
                        onChange={handleOverwriteOrAddNewFileCheckboxesForDuplicatedFiles(
                          index,
                          "addNewFile",
                        )}
                        style={{
                          color: "black",
                          opacity: filesUploading ? 0.4 : 1,
                        }}
                      />
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderAddItemModal = () => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            display: "flex",
            minWidth: "700px",
          }}
        >
          <Container
            sx={{
              width: "700px",
            }}
          >
            <Stack
              elevation={12}
              style={{
                padding: "25px 25px 50px 25px",
                background: "white",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack style={{ gap: 25 }}>
                <Stack
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                  }}
                >
                  <CustomTypography title message="Upload Files to" />
                  {path || "/"}
                </Stack>

                {filesUploadErrors === null &&
                  renderDropzone(getRootProps, getInputProps, isDragActive)}
              </Stack>
              <div>
                {filesUploadErrors && (
                  <Alert
                    variant="filled"
                    severity="error"
                    style={{
                      fontFamily: "tmwCustomFont",
                      letterSpacing: 1,
                      marginTop: 20,
                      height: 250,
                      background: "white",
                      color: "red",
                      fontSize: 11,
                      fontWeight: 400,
                      border: "1px solid red",
                      borderRadius: 0,
                    }}
                    icon={
                      <img
                        style={{
                          width: "20px",
                          height: "20px",
                          marginRight: "5px",
                        }}
                        src={tmwWarningIcon}
                      />
                    }
                  >
                    <span>Validation Errors</span>
                    <ul style={{ padding: 0, width: 600 }}>
                      {filesUploadErrors.map((error, index) => {
                        return (
                          <li key={`error-${index}`}>
                            <u>{error.fileName}</u>:
                            <ul>
                              {error.errors.map((error, index) => {
                                return <li key={`error-${index}`}>{error}</li>;
                              })}
                            </ul>
                          </li>
                        );
                      })}
                    </ul>
                  </Alert>
                )}
              </div>
              <Stack
                style={{
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 10,
                  margin: "35px 0 -15px 0",
                }}
              >
                <CustomButton
                  label={!filesUploadErrors ? "Cancel" : "Close"}
                  smallButton
                  onClick={() => {
                    setSelectedFilesForUpload(null);
                    setAddItemModalVisible(false);
                    dispatch(thunks.uploadFilesErrorReset());
                  }}
                />
                {!filesUploadErrors && (
                  <CustomButton
                    label="Upload"
                    smallButton
                    greenButton
                    onClick={() => handleUploadFiles()}
                    disabled={
                      !selectedFilesForUpload ||
                      filesUploading ||
                      (duplicatedFilesForUpload &&
                        duplicatedFilesForUpload.length > 0 &&
                        !duplicatedFilesForUpload.every(
                          (obj) => obj.overwrite || obj.addNewFile,
                        )) ||
                      (uploadFilesTotalSize / (1000 * 1000)).toFixed(2) >
                        spacesDOSettings?.maxUploadLimitMB
                    }
                    loader={
                      filesUploading && (
                        <CircularProgress
                          color="inherit"
                          size={10}
                          style={{ marginLeft: "4px" }}
                        />
                      )
                    }
                  />
                )}
              </Stack>
            </Stack>
          </Container>
          {duplicatedFilesForUpload && !filesUploadErrors && (
            <Container
              sx={{
                width: "500px",
              }}
            >
              <Stack
                elevation={12}
                style={{
                  padding: "15px 25px 15px 15px",
                  height: "100%",
                  width: "100%",
                  background: "white",
                  border: "1px solid #E6E6E6",
                }}
              >
                <Stack
                  style={{
                    alignItems: "center",
                    justifyContent: "flex-start",
                    fontSize: 12,
                    width: "100%",
                    gap: 2,
                  }}
                >
                  <CustomTypography
                    message="Duplicated Files:"
                    style={{ fontWeight: "bold" }}
                  />
                  <CustomTypography
                    message="Please make a choice for each file"
                    style={{ fontSize: 8 }}
                  />
                  <Stack
                    style={{
                      marginTop: 10,
                      maxHeight: "400px",
                      overflowY: "auto",
                      width: "100%",
                    }}
                  >
                    {renderDuplcatedFormDataFiles(duplicatedFilesForUpload)}
                  </Stack>
                  <CustomTypography
                    message="Note: Click on Overwrite/New File header if you want this choice for all files."
                    style={{ fontSize: 8, fontWeight: "bold", marginTop: 5 }}
                  />
                  <CustomTypography
                    message="1. Overwrite: Choosing this option will overwrite the existing file."
                    style={{ fontSize: 8 }}
                  />
                  <CustomTypography
                    message="2. New File: Choosing this option will create a new file appending to the filename a counter between brackets."
                    style={{ fontSize: 8 }}
                  />
                </Stack>
              </Stack>
            </Container>
          )}
        </Box>
      </div>
    );
  };

  // Delete Folder / File - (Single) - Modal
  const renderDeleteItemModal = () => {
    const deletePrompt =
      itemToDelete.type === "file" ? (
        <Stack>
          Are you sure you want to delete this file?{" "}
          <span
            style={{
              fontWeight: 600,
              marginTop: "25px",
              fontSize: 9,
              textAlign: "center",
            }}
          >
            {itemToDelete.item.name}
          </span>
        </Stack>
      ) : (
        <Stack>
          Are you sure you want to delete this folder and all of its contents?
          <span
            style={{
              fontWeight: 600,
              marginTop: "25px",
              fontSize: 9,
              textAlign: "center",
            }}
          >
            {itemToDelete.item.name}
          </span>
        </Stack>
      );

    // Table Column Prompt
    const tableColumnPrompt =
      itemToDelete.type === "file" ? "File Name" : "Folder name";

    // Prepare Item to Delete Array
    const itemToDeleteArray = [itemToDelete];

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            minWidth: "500px",
          }}
        >
          <Container
            sx={{
              width: "500px",
            }}
          >
            <Stack
              elevation={12}
              style={{
                padding: "25px 25px 50px 25px",
                background: "white",
                border: "1px solid #E6E6E6",
              }}
            >
              <CustomTypography message={deletePrompt} />
              <Stack
                style={{
                  maxHeight: "300px",
                  height: "100%",
                  overflowY: "auto",
                  marginTop: 10,
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                }}
              >
                <TableContainer
                  style={{ border: "1px solid #E6E6E6", borderRadius: "1px" }}
                >
                  <Table stickyHeader size="small" aria-label="a dense table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{
                            fontFamily: "tmwCustomFont",
                            letterSpacing: 1,
                            fontSize: 8,
                          }}
                        >
                          N°
                        </TableCell>
                        <TableCell
                          style={{
                            fontFamily: "tmwCustomFont",
                            letterSpacing: 1,
                            fontSize: 8,
                          }}
                        >
                          {tableColumnPrompt}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemToDeleteArray.map((file, index) => (
                        <TableRow
                          key={file}
                          sx={{
                            "& td, & th": {
                              padding: "2px 8px", // smaller vertical and horizontal padding
                            },
                          }}
                        >
                          <TableCell
                            align="left"
                            style={{
                              fontFamily: "tmwCustomFont",
                              letterSpacing: 1,
                              fontSize: 11,
                            }}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell
                            align="left"
                            style={{
                              fontFamily: "tmwCustomFont",
                              letterSpacing: 1,
                              fontSize: 11,
                              whiteSpace: "normal", // allow text to wrap
                              wordBreak: "break-word", // break long words if needed
                            }}
                          >
                            {file.item.name}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
              <Stack
                style={{
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 10,
                  margin: "35px 0 -15px 0",
                }}
              >
                <CustomButton
                  label="Cancel"
                  smallButton
                  onClick={() => {
                    setItemToDelete(null);
                    setDeleteModalOpened(false);
                  }}
                />
                <CustomButton
                  label="Delete"
                  smallButton
                  redButton
                  onClick={() => {
                    if (itemToDelete.type === "file") {
                      deleteItem(path + itemToDelete.item.name);
                      setSelectedItemsToDelete([]);
                    } else {
                      deleteFolder(path + itemToDelete.item.name);
                      setSelectedItemsToDelete([]);
                    }
                  }}
                  disabled={itemDeleting}
                  loader={
                    itemDeleting && (
                      <CircularProgress
                        color="inherit"
                        size={10}
                        style={{ marginLeft: "4px" }}
                      />
                    )
                  }
                />
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  // Delete Files - (Multiple) - Modal
  const renderDeleteMultipleSelectedFilesModal = () => {
    // Delete Prompt Message
    const deletePrompt =
      "Are you sure you want to delete those selected files?";

    // Generate Files Path Array
    const filesPathArray = selectedItemsToDelete?.map(
      (selectedItemToDelete) => selectedItemToDelete.key,
    );

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            minWidth: "500px",
          }}
        >
          <Container
            sx={{
              width: "500px",
            }}
          >
            <Stack
              elevation={12}
              style={{
                padding: "25px 25px 50px 25px",
                background: "white",
                border: "1px solid #E6E6E6",
              }}
            >
              <CustomTypography message={deletePrompt} />
              <Stack
                style={{
                  maxHeight: "300px",
                  height: "100%",
                  overflowY: "auto",
                  marginTop: 10,
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                }}
              >
                <TableContainer
                  style={{ border: "1px solid #E6E6E6", borderRadius: "1px" }}
                >
                  <Table stickyHeader size="small" aria-label="a dense table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{
                            fontFamily: "tmwCustomFont",
                            letterSpacing: 1,
                            fontSize: 8,
                          }}
                        >
                          N°
                        </TableCell>
                        <TableCell
                          style={{
                            fontFamily: "tmwCustomFont",
                            letterSpacing: 1,
                            fontSize: 8,
                          }}
                        >
                          File Name
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedItemsToDelete.map((file, index) => (
                        <TableRow
                          key={file}
                          sx={{
                            "& td, & th": {
                              padding: "2px 8px", // smaller vertical and horizontal padding
                            },
                          }}
                        >
                          <TableCell
                            align="left"
                            style={{
                              fontFamily: "tmwCustomFont",
                              letterSpacing: 1,
                              fontSize: 11,
                            }}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell
                            align="left"
                            style={{
                              fontFamily: "tmwCustomFont",
                              letterSpacing: 1,
                              fontSize: 11,
                              whiteSpace: "normal", // allow text to wrap
                              wordBreak: "break-word", // break long words if needed
                            }}
                          >
                            {file.name}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
              <CustomTypography
                message={`Total: ${selectedItemsToDelete?.length}`}
                style={{ marginTop: 5, fontSize: 10 }}
              />
              <Stack
                style={{
                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                {multipleItemsDeleting && (
                  <div>
                    <CircularProgress
                      color="inherit"
                      size={11}
                      style={{ marginRight: 5, color: "red" }}
                    />
                    <CustomTypography
                      message={`Deleting`}
                      style={{ fontSize: 11, color: "red" }}
                    />
                  </div>
                )}
              </Stack>

              <Stack
                style={{
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 10,
                  margin: "35px 0 -15px 0",
                }}
              >
                <CustomButton
                  label="Cancel"
                  smallButton
                  onClick={() => {
                    setDeleteMultipleItemsModalOpened(false);
                  }}
                />
                <CustomButton
                  label="Delete"
                  smallButton
                  redButton
                  onClick={() => {
                    deleteMultipleItems(filesPathArray);
                  }}
                  disabled={multipleItemsDeleting}
                  loader={
                    multipleItemsDeleting && (
                      <CircularProgress
                        color="inherit"
                        size={10}
                        style={{ marginLeft: "4px" }}
                      />
                    )
                  }
                />
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  // Handle Select (Checkbox) Multiple Files for Delete
  const handleSelectMultipleFilesForDeleteOnChange = (value) => {
    setSelectedItemsToDelete((prev) => {
      if (prev.includes(value)) {
        // If value is already selected, remove it
        return prev.filter((item) => item !== value);
      } else {
        // If value is not selected, add it
        return [...prev, value];
      }
    });
  };

  //
  // Image Preview - Tooltip Content & Handle
  //

  // Handle Is Image for Image Preview Tooltip (checking extension)
  const isImage = (fileName) => {
    const parts = fileName.split(".");
    const extension = parts[parts.length - 1];
    return ["jpg", "jpeg", "png", "gif", "svg"].includes(
      extension.toLowerCase(),
    );
  };

  // Tooltip content
  const tooltipContent = (fileName, item) => {
    if (isImage(fileName)) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src={`${spacesDOSettings?.url}/${item.key}`}
            alt={item.name}
            style={{
              width: 180,
              borderRadius: 8,
              display: "block",
              marginBottom: 1,
            }}
          />
          <span
            style={{
              color: "black",
              display: "block",
              wordBreak: "break-all",
              textAlign: "center",
            }}
          >
            {fileName}
          </span>
        </div>
      );
    }

    // Non-image files
    return (
      <span
        style={{
          color: "black",
          display: "block",
          wordBreak: "break-all",
          textAlign: "center",
        }}
      >
        {fileName}
      </span>
    );
  };

  return (
    <>
      <Seo title="Dashboard: Assets" />
      <Stack
        style={{
          margin: "25px 50px",
          fontFamily: "tmwCustomFont",
        }}
      >
        <Stack
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <CustomTypography
            message="Tomorrow Assets"
            title
            style={{ margin: "10px 0 10px 0" }}
          />
        </Stack>
        <div>{renderSearchBar()}</div>
        {renderButtonSection()}
        <CustomTypography
          message={path || "/"}
          style={{ margin: "15px 0 0 0" }}
        />
        {!itemsLoading && (
          <CustomTypography
            message={
              filteredItems?.length
                ? filteredItems.length === 0
                  ? "0 Items"
                  : filteredItems.length === 1
                    ? "1 Item"
                    : `${filteredItems?.length} Items`
                : ""
            }
            style={{ margin: "15px 0 15px 0" }}
          />
        )}
        {!itemsLoading && path && (
          <Stack style={{ margin: "5px 0 5px 0" }}>{renderBackButton()}</Stack>
        )}
        {itemsLoading ? (
          <Stack
            style={{
              alignItems: "center",
              justifyContent: "center",
              height: "50vh",
            }}
          >
            <CircularProgress color="inherit" size={35} />
          </Stack>
        ) : (
          <Stack
            style={{
              margin: "15px 30px",
              padding: "0 15px",
              gap: 10,
              display: "grid",
              gridAutoFlow: "column", // top → bottom
              gridAutoRows: "auto",
              gridTemplateRows: `repeat(${numRows}, auto)`, // dynamic based on filteredItems
              gridTemplateColumns: `repeat(auto-fill, ${columnWidth}px)`, // fixed width columns
              alignItems: "start",
            }}
          >
            {!!filteredItems?.length &&
              filteredItems.map((item) => (
                <Stack
                  key={item.key}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 10,
                    textDecoration:
                      hoveredElement === item.key ? "underline" : "none",
                    whiteSpace: "nowrap",
                    overflow: "visible",
                  }}
                >
                  {item.isFolder ? (
                    <>
                      <Stack>
                        <Folder size={20} />
                      </Stack>
                      <Stack
                        style={{
                          fontSize: 14,
                          flexDirection: "row",
                        }}
                      >
                        <Tooltip
                          title={item.name}
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontFamily: "tmwCustomFont",
                                letterSpacing: 1,
                                fontSize: 10,
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                p: 0.5,
                                color: "black",
                              },
                            },
                          }}
                        >
                          <span
                            style={{
                              cursor: "pointer",
                              fontSize: 14,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "inline-block",
                              maxWidth: "250px",
                              verticalAlign: "middle",
                            }}
                            onClick={() => {
                              handleCloseAddDirectory();
                              navigateTo(item.name);
                            }}
                            onMouseEnter={() => setHoveredElement(item.key)}
                            onMouseLeave={() => setHoveredElement()}
                          >
                            {item.name}
                          </span>
                        </Tooltip>
                        {path.split("/").length > 1 &&
                          deleteItems &&
                          hasAdminPermission && (
                            <DeleteOutline
                              style={{
                                fontSize: 19,
                                cursor: "pointer",
                                marginLeft: "5px",
                                color: "#FF0000",
                              }}
                              onClick={() => {
                                setDeleteModalOpened(true);
                                setItemToDelete({ type: "folder", item });
                              }}
                            />
                          )}
                      </Stack>
                    </>
                  ) : (
                    <Stack
                      style={{ flexDirection: "row", gap: 10 }}
                      alignItems="center"
                    >
                      <Stack>
                        <File size={20} />
                      </Stack>
                      <Tooltip
                        open={hoveredImage === item.name}
                        title={tooltipContent(item.name, item)}
                        placement="top"
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "white",
                              border: "1px solid #ccc",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              p: 0.5,
                              fontFamily: "tmwCustomFont",
                              letterSpacing: 1,
                              fontSize: 10,
                            },
                          },
                        }}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            fontSize: 14,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "inline-block",
                            maxWidth: "250px",
                            verticalAlign: "middle",
                          }}
                          onClick={() => {
                            handleCloseAddDirectory();
                            viewItem(item.key);
                          }}
                          onMouseEnter={() => {
                            setHoveredElement(item.key);
                            timerRef.current = setTimeout(
                              () => setHoveredImage(item.name),
                              hoverImageDelay,
                            );
                          }}
                          onMouseLeave={() => {
                            setHoveredElement();
                            clearTimeout(timerRef.current);
                            setHoveredImage(null);
                          }}
                        >
                          {item.name}
                        </span>
                      </Tooltip>
                      {path.split("/").length > 1 && deleteItems && (
                        <Stack direction="row" alignItems="center">
                          <Checkbox
                            size="small"
                            sx={{ p: 0, m: 0 }}
                            style={{ color: "black" }}
                            checked={selectedItemsToDelete.includes(item)}
                            onChange={() =>
                              handleSelectMultipleFilesForDeleteOnChange(item)
                            }
                          />
                          <Delete
                            style={{
                              fontSize: 20,
                              cursor: "pointer",
                              color: "#FF0000",
                            }}
                            onClick={() => {
                              setDeleteModalOpened(true);
                              setItemToDelete({ type: "file", item });
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Stack>
              ))}
            {!!addingDirectory && (
              <Stack
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                }}
                ref={newDirectoryRef}
              >
                <Stack>
                  <Folder size={20} />
                </Stack>
                <input
                  style={{ fontSize: 14 }}
                  value={newDirectoryName}
                  onChange={(e) => setNewDirectoryName(e.target.value)}
                />
                <Stack flexDirection="row">
                  <CloseOutlined
                    style={{
                      cursor: "pointer",
                    }}
                    onClick={handleCloseAddDirectory}
                  />
                  <Check
                    style={{
                      color: newDirectoryName === "" ? "#E6E6E6" : "black",
                      cursor: newDirectoryName === "" ? "default" : "pointer",
                    }}
                    onClick={() => {
                      if (newDirectoryName !== "") {
                        uploadFolder(path + newDirectoryName);
                        handleCloseAddDirectory();
                      }
                    }}
                    disabled={newDirectoryName === ""}
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
        {!filteredItems.length && !itemsLoading && !addingDirectory && (
          <Stack
            style={{
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "40vh",
            }}
          >
            <CustomTypography message="Empty folder" />
          </Stack>
        )}
        {addItemModalVisible && renderAddItemModal()}
        {deleteModalOpened && renderDeleteItemModal()}
        {deleteMultipleItemsModalOpened &&
          renderDeleteMultipleSelectedFilesModal()}
        {renderAssetsHistoryModal()}
      </Stack>
    </>
  );
};

export default withAssetsGuard(Assets);
